import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { PiTree, PiPlant, PiMapPin, PiGridFour, PiSquaresFour, PiTreeStructure, PiClipboardText } from 'react-icons/pi';
import { ReactComponent as ListIcon } from '../../images/book-library-svgrepo-com.svg';
import { CheckCircle, CheckCircleOutline, CheckCircleOutlineOutlined } from '@mui/icons-material';
import { BiCheckCircle, BiErrorCircle, BiStopCircle } from 'react-icons/bi';
import { FaBridgeCircleCheck, FaX } from 'react-icons/fa6';
import { FaCheck } from 'react-icons/fa';

interface DialogEspeciesProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    especies: any[];
    setores: any[];
    unidades: any[];
    subunidades: any[];
    arvores: any[];
    loading: boolean;
    onSelect?: (feature: any) => void;
}

export default function DialogEspecies(props: DialogEspeciesProps) {
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState<any>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    // Processamento para achatar os dados de Fustes e RNII para as tabelas
    const allFustes = React.useMemo(() => {
        return props.arvores.flatMap(arvore =>
            (arvore.properties?.fustes || []).map((fuste: any) => ({
                ...fuste,
                plaqueta: arvore.properties?.plaqueta,
                arvore_feature: arvore // Referência para o zoom
            }))
        );
    }, [props.arvores]);

    const allRNII = React.useMemo(() => {
        return props.arvores.flatMap(arvore =>
            (arvore.properties?.fustes || []).flatMap((fuste: any) =>
                (fuste.rnii || []).map((rnii: any) => ({
                    ...rnii,
                    plaqueta: arvore.properties?.plaqueta,
                    fuste_codigo: fuste.codigo,
                    arvore_feature: arvore // Referência para o zoom
                }))
            )
        );
    }, [props.arvores]);

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-content-end">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Pesquisar..." />
                </span>
            </div>
        );
    };

    const actionTemplate = (rowData: any) => {
        return (
            <Button
                icon="pi pi-search-plus"
                rounded
                text
                severity="secondary"
                onClick={() => props.onSelect?.(rowData.arvore_feature || rowData)}
                tooltip="Ver no mapa"
            />
        );
    };

    const headerTemplate = (icon: React.ReactNode, label: string) => (
        <div className="flex align-items-center" style={{ gap: '8px' }}>
            {icon}
            <span>{label}</span>
        </div>
    );

    console.log('DialogEspecies render', { especies: props.especies, setores: props.setores, unidades: props.unidades, subunidades: props.subunidades, arvores: props.arvores });

    return (
        <Dialog
            header={<div style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}><ListIcon width={32} height={32} /> <div>PESQUISA DE INVENTÁRIO</div></div>}
            visible={props.visible}
            style={{ width: '90vw', maxWidth: '1000px' }}
            onHide={() => props.setVisible(false)}
            maximizable
            modal
            draggable={false}
            resizable={false}
        >
            <TabView scrollable>
                <TabPanel header={headerTemplate(<PiMapPin />, "Setores")}>
                    <DataTable value={props.setores} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['properties.nome', 'id']}
                        header={renderHeader()} emptyMessage="Nenhum setor encontrado." stripedRows>
                        <Column field="id" header="ID" sortable />
                        <Column field="properties.setor" header="Setor" sortable />
                        <Column field="properties.tipo" header="Tipo" sortable />
                        <Column field="properties.area_ha" header="Área (ha)" sortable body={(r) => r.properties?.area_ha?.toFixed(2)} />
                        <Column field="properties.margem" header="Margem" sortable />
                        <Column field="properties.zona" header="Zona" sortable />
                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={headerTemplate(<PiGridFour />, "Unidades")}>
                    <DataTable value={props.unidades} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['properties.nome', 'id']}
                        header={renderHeader()} emptyMessage="Nenhuma unidade encontrada." stripedRows>
                        <Column field="properties.codigo" header="ID" sortable />
                        <Column field="properties.municipio" header="Municipio" sortable />
                        <Column field="properties.localidade" header="Localidade" sortable />
                        <Column field="properties.acesso" header="Acesso" sortable />
                        <Column field="properties.acessibilidade" header="Acessibilidade" sortable />
                        <Column field="properties.subbosque" header="Subbosque" sortable />
                        <Column field="properties.cobertura" header="Cobertura" sortable />
                        <Column field="properties.antropismo" header="Antropismo" sortable />
                        <Column field="properties.fisiografia" header="Fisiografia" sortable />
                        <Column field="properties.erosao" header="Erosão" body={(r) => r.properties?.erosao ? <FaCheck className="text-green-500" size={16} /> : <FaX className="text-red-500" size={16} />} sortable />

                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={headerTemplate(<PiSquaresFour />, "Subunidades")}>
                    <DataTable value={props.subunidades} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['properties.nome', 'id']}
                        header={renderHeader()} emptyMessage="Nenhuma subunidade encontrada (tente dar mais zoom no mapa)." stripedRows>
                        <Column field="properties.unidade_municipio" header="Municipio" sortable />
                        <Column field="properties.unidade_codigo" header="Unidade" sortable />
                        <Column field="properties.codigo" header="ID" sortable body={(r) => r.properties?.unidade_codigo?.toString().padStart(3, '0') + '-' + r.properties?.codigo?.toString().padStart(3, '0')} />
                        <Column field="properties.arvores" header="Árvores" body={(r) => r.properties?.arvores?.features?.length || 0} sortable />
                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={headerTemplate(<PiTree />, "Árvores")}>
                    <DataTable value={props.arvores} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['properties.plaqueta', 'id', 'properties.especie_nome']}
                        header={renderHeader()} emptyMessage="Nenhuma árvore encontrada (tente dar mais zoom no mapa)." stripedRows>
                        <Column field="properties.plaqueta" header="Plaqueta" sortable />
                        <Column field="properties.especie.forma_vida" header="Forma" sortable />

                        <Column field="properties.especie.nome_popular" header="Espécie" sortable />
                        <Column field="properties.especie.origem" header="Origem" sortable />

                        <Column field="properties.fustes" header="Fustes" body={(r) => r.properties?.fustes?.length || 0}sortable />


                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={headerTemplate(<PiPlant />, "Espécies")}>
                    <DataTable value={props.especies} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['nome_popular', 'nome_cientifico', 'familia']}
                        header={renderHeader()} emptyMessage="Nenhuma espécie encontrada." stripedRows>
                        <Column field="nome_popular" header="Nome Popular" sortable />
                        <Column field="nome_cientifico" header="Nome Científico" sortable body={(r) => <i>{r.nome_cientifico}</i>} />
                        <Column field="familia" header="Família" sortable />
                        <Column field="genero" header="Gênero" sortable />
                        <Column field="forma_vida" header="Forma de Vida" sortable />
                        <Column field="fenologia" header="Fenologia" sortable />
                        <Column field="polinizacao" header="Polinização" sortable />
                        <Column field="origem" header="Origem" sortable />
                    </DataTable>
                </TabPanel>

                <TabPanel header={headerTemplate(<PiTreeStructure />, "Fuste")}>
                    <DataTable value={allFustes} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['plaqueta', 'codigo']}
                        header={renderHeader()} emptyMessage="Nenhum fuste encontrado." stripedRows>
                        <Column field="plaqueta" header="Plaqueta (Árvore)" sortable />
                        <Column field="codigo" header="Código Fuste" sortable />
                        <Column field="rnii.cap" header="CAP" body={(r) => r.rnii?.[0]?.cap } sortable />
                        <Column field="rnii.altura_total" header="Alt. Total" body={(r) => r.rnii?.[0]?.altura_total } sortable />
                        <Column field="rnii.altura_fuste" header="Alt. Fuste" body={(r) => r.rnii?.[0]?.altura_fuste } sortable />
                        <Column field="rnii.qf" header="QF" body={(r) => r.rnii?.[0]?.qf } sortable />
                        <Column field="rnii.ps" header="PS" body={(r) => r.rnii?.[0]?.ps } sortable />
                        <Column field="rnii.nl" header="NL" body={(r) => r.rnii?.[0]?.nl } sortable />
                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                {/* <TabPanel header={headerTemplate(<PiClipboardText />, "RNII")}>
                    <DataTable value={allRNII} paginator rows={5} rowsPerPageOptions={[5, 10, 25]}
                        loading={props.loading} filters={filters} globalFilterFields={['plaqueta', 'fuste_codigo']}
                        header={renderHeader()} emptyMessage="Nenhum registro RNII encontrado." stripedRows>
                        <Column field="plaqueta" header="Plaqueta" sortable />
                        <Column field="fuste_codigo" header="Fuste" sortable />
                        <Column field="cap" header="CAP" sortable />
                        <Column field="altura_total" header="Alt. Total" sortable />
                        <Column field="altura_fuste" header="Alt. Fuste" sortable />
                        <Column field="qf" header="QF" sortable />
                        <Column field="ps" header="PS" sortable />
                        <Column field="nl" header="NL" sortable />
                        <Column body={actionTemplate} headerStyle={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel> */}
            </TabView>
        </Dialog>
    );
}